import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { useTranslation } from "../../hooks/useTranslation";
import { ConsultationMode } from "./types";
const pillSelectSx = {
    borderRadius: "10px",
    bgcolor: "background.paper",
    fontSize: 13,
    fontWeight: 500,
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "divider",
        borderRadius: "10px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "text.disabled",
    },
    "& .MuiSvgIcon-root.MuiSelect-icon": { fontSize: 18, color: "text.secondary" },
};
export function DoctorStripFilters({ filters, isMobile = false, }) {
    const { t } = useTranslation("appointment");
    const { consultationMode, specializationFilter, locationFilter, specializations, locations, showDoctorFilters = true, showConsultationMode = true, availableModes = [ConsultationMode.OFFLINE], allowAllLocations = true, showFullAddress = false, showLocation = showDoctorFilters, showSpecialization = showDoctorFilters, onConsultationModeChange, onSpecializationChange, onLocationChange, } = filters;
    const specializationLabel = t("specialization", "Specialization");
    const locationLabel = t("location", "Location");
    const labelForLocation = (loc) => showFullAddress && loc.fullAddress ? loc.fullAddress : loc.city;
    const showOfflineBtn = showConsultationMode && availableModes.includes(ConsultationMode.OFFLINE);
    const showOnlineBtn = showConsultationMode && availableModes.includes(ConsultationMode.ONLINE);
    const modeToggle = (showOfflineBtn || showOnlineBtn) && (_jsxs(Box, { role: "radiogroup", "aria-label": t("consultationMode", "Consultation mode"), sx: { display: "flex", gap: 0.75, flexShrink: 0 }, children: [showOfflineBtn && (_jsx(ModeBtn, { label: t("inPerson", "In-Person"), icon: _jsx(PersonOutlineIcon, { sx: { fontSize: 16 } }), active: consultationMode === ConsultationMode.OFFLINE, onClick: () => onConsultationModeChange(ConsultationMode.OFFLINE) })), showOnlineBtn && (_jsx(ModeBtn, { label: t("online", "Online"), icon: _jsx(VideocamOutlinedIcon, { sx: { fontSize: 16 } }), active: consultationMode === ConsultationMode.ONLINE, onClick: () => onConsultationModeChange(ConsultationMode.ONLINE) }))] }));
    const specializationSelect = showSpecialization && (_jsxs(Select, { size: "small", value: specializationFilter, onChange: (e) => onSpecializationChange(e.target.value), displayEmpty: true, IconComponent: KeyboardArrowRightIcon, renderValue: (val) => (_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 0.75, sx: { minWidth: 0, overflow: "hidden" }, children: [_jsx(GridViewOutlinedIcon, { sx: { fontSize: 15, color: "text.secondary", flexShrink: 0 } }), _jsx(Typography, { variant: "body2", noWrap: true, sx: { fontSize: 13, fontWeight: 500, minWidth: 0 }, children: val === "all"
                        ? specializationLabel
                        : (specializations.find((s) => s === val) ?? specializationLabel) })] })), sx: {
            ...pillSelectSx,
            width: isMobile ? "100%" : 180,
            "& .MuiSelect-select": { py: "7px", pl: 1.5 },
        }, children: [_jsx(MenuItem, { value: "all", sx: { fontSize: 13 }, children: t("allSpecializations", "All Specializations") }), specializations.map((s) => (_jsx(MenuItem, { value: s, sx: { fontSize: 13 }, children: s }, s)))] }));
    const locationSelect = showLocation && (_jsxs(Select, { size: "small", value: locationFilter, onChange: (e) => onLocationChange(e.target.value), displayEmpty: true, IconComponent: KeyboardArrowRightIcon, renderValue: (val) => (_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 0.75, sx: { minWidth: 0, overflow: "hidden" }, children: [_jsx(LocationOnOutlinedIcon, { sx: {
                        fontSize: isMobile ? 13 : 15,
                        color: "primary.main",
                        flexShrink: 0,
                    } }), _jsx(Typography, { variant: "body2", noWrap: true, sx: { fontSize: 13, fontWeight: 500, minWidth: 0 }, children: (() => {
                        if (val === "all")
                            return locationLabel;
                        const loc = locations.find((a) => String(a.id) === val);
                        return loc ? labelForLocation(loc) : locationLabel;
                    })() })] })), sx: {
            ...pillSelectSx,
            width: isMobile ? "100%" : 200,
            "& .MuiSelect-select": { py: "7px", pl: isMobile ? 1.25 : 1.5 },
        }, children: [allowAllLocations && (_jsx(MenuItem, { value: "all", sx: { fontSize: 13 }, children: t("allLocations", "All Locations") })), locations.map((a) => (_jsx(MenuItem, { value: String(a.id), sx: { fontSize: 13, whiteSpace: "normal", maxWidth: 360 }, children: labelForLocation(a) }, a.id)))] }));
    if (isMobile) {
        return (_jsxs(Stack, { direction: "row", flexWrap: "wrap", gap: 1, sx: { width: "100%" }, children: [modeToggle, specializationSelect, locationSelect] }));
    }
    return (_jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", sx: { flexShrink: 0 }, children: [modeToggle, specializationSelect, locationSelect] }));
}
function ModeBtn({ label, icon, active, onClick, }) {
    return (_jsxs(Box, { component: "button", type: "button", role: "radio", "aria-checked": active, onClick: onClick, sx: {
            display: "inline-flex",
            alignItems: "center",
            gap: 0.625,
            py: "7px",
            px: 1.5,
            borderRadius: 1.25,
            fontSize: 13,
            fontWeight: active ? 600 : 500,
            border: 1,
            borderColor: active ? "secondary.main" : "divider",
            cursor: "pointer",
            bgcolor: active ? "secondary.main" : "background.paper",
            color: active ? "secondary.contrastText" : "text.secondary",
            transition: "all 0.15s",
            whiteSpace: "nowrap",
            "&:hover": {
                borderColor: "secondary.main",
                bgcolor: active ? "secondary.dark" : "action.hover",
            },
            "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "secondary.main",
                outlineOffset: 1,
            },
        }, children: [icon, label] }));
}
